// app/src/lib/scale/instrument.js — single entry point for two cross-cutting
// concerns every Phase 1–4 mutation needs:
//
//   1. §5.1/§5.2 plan-tier gate: read-only accounts must not be allowed to mutate.
//   2. §5.5 audit log:           every create / update / delete is recorded.
//
// Usage:
//
//   await mutate({
//     entityType: 'invoice',
//     entityId:   invoiceId,
//     action:     'create',           // 'create' | 'update' | 'delete'
//     before,                         // optional, omitted for create
//     after,                          // optional, omitted for delete
//   }, async () => {
//     await db.invoices.put(row);
//   });
//
// The helper:
//   - calls `plan.requireWritable()` first (throws code='READ_ONLY' if not),
//   - awaits the mutation,
//   - calls `audit.record({...})` after success (fail-soft — never blocks).
//
// For trivial cases where the caller doesn't need a wrapper, `gate()` and
// `record()` can be called directly. The wrapper exists so we have ONE place to
// add future cross-cutting concerns (e.g. outbound webhook fan-out).

import { requireWritable } from './plan.js';
import * as audit from './audit.js';
import { markBooksDirty } from '../backup/sharedSnapshot.js';

/**
 * @typedef {Object} MutateMeta
 * @property {string} entityType
 * @property {string} entityId
 * @property {'create'|'update'|'delete'|'approve'|'reject'|'export'|'invite'|'revoke'} action
 * @property {object=} before
 * @property {object=} after
 * @property {object=} meta
 * @property {boolean=} skipPlanGate - set true for system-side maintenance writes (rare).
 */

/**
 * @template T
 * @param {MutateMeta} info
 * @param {() => Promise<T>} fn
 * @returns {Promise<T>}
 */
export async function mutate(info, fn) {
  if (!info?.skipPlanGate) await requireWritable();
  const result = await fn();
  // audit.record is fail-soft; never blocks the mutation.
  await audit.record({
    action: info.action,
    entityType: info.entityType,
    entityId: info.entityId,
    before: info.before,
    after: info.after,
    meta: info.meta,
  });
  // Team accounts: schedule an owner shared-snapshot upload (debounced, no-op
  // for viewers). Never blocks the mutation.
  try { markBooksDirty(); } catch { /* non-fatal */ }
  return result;
}

/** Bare plan gate for callers that don't need an audit row (e.g. read-throughs). */
export { requireWritable as gate };

/** Bare audit record for after-the-fact instrumentation. */
export const record = audit.record;
